// src/utils/manualAuth.js
// Single source of truth for iOS "manual auth" stored in localStorage.
//
// Fixes:
// - Native iOS sessions are NOT treated as valid forever anymore.
// - If the JWT contains an `exp`, we honor it.
// - If there is no usable exp, we fall back to obtainedAt/expiresIn when present.
// - This avoids "ghost logged-in" UI after app resumes or long inactivity.

import { LOOP_NATIVE_AUTH_RESTORE_KEY, clearAuthScopedStorage, emitStorageScopeChanged } from "../auth/authStorage";

export function shouldAllowNativeSessionRestore() {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(LOOP_NATIVE_AUTH_RESTORE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function setNativeSessionRestoreAllowed(value) {
  try {
    if (typeof localStorage === "undefined") return;
    if (value) localStorage.setItem(LOOP_NATIVE_AUTH_RESTORE_KEY, "1");
    else localStorage.removeItem(LOOP_NATIVE_AUTH_RESTORE_KEY);
  } catch (_) {}
}

function isNativeIosSource(source) {
  const s = (source || "").toString().trim().toLowerCase();
  return s === "ios-native-email" || s === "ios-native-google" || s === "ios-native-apple";
}

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

function getJwtExpiryMs(token) {
  try {
    const payload = decodeJwtPayload(token);
    const exp = payload && payload.exp ? Number(payload.exp) : NaN;
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return exp * 1000;
  } catch (_) {
    return null;
  }
}

export function getManualAuth() {
  try {
    if (typeof window !== "undefined" && window.__MANUAL_AUTH__ && window.__MANUAL_AUTH__.idToken) {
      return window.__MANUAL_AUTH__;
    }
  } catch (_) {}

  try {
    const raw = localStorage.getItem("__MANUAL_AUTH__");
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && obj.idToken) return obj;
  } catch (_) {}

  return null;
}

function manualAuthIdentity(ma) {
  const payload = decodeJwtPayload(ma?.idToken) || {};
  return {
    uid: ma?.uid || payload.user_id || payload.uid || payload.sub || null,
    email: ma?.email || payload.email || payload.firebase?.identities?.email?.[0] || "",
    displayName: ma?.displayName || ma?.name || payload.name || payload.displayName || "",
  };
}

export function getEffectiveManualAuthSession() {
  try {
    const ma = getManualAuth();
    if (!ma || !isManualAuthLikelyValid(ma)) {
      return { loggedIn: false, user: null, manualAuth: null, source: "manual-auth-missing" };
    }

    const identity = manualAuthIdentity(ma);
    const ok = Boolean(identity.uid || identity.email);
    if (!ok) {
      return { loggedIn: false, user: null, manualAuth: ma, source: ma.source || "manual-auth-invalid-identity" };
    }

    return {
      loggedIn: true,
      source: ma.source || "manual-auth",
      manualAuth: { ...ma, uid: identity.uid || ma.uid || null, email: identity.email || ma.email || "", displayName: identity.displayName || ma.displayName || ma.name || "" },
      user: {
        uid: identity.uid || ma.uid || null,
        email: identity.email || ma.email || "",
        displayName: identity.displayName || ma.displayName || ma.name || "",
      },
    };
  } catch (_) {
    return { loggedIn: false, user: null, manualAuth: null, source: "manual-auth-error" };
  }
}

function dispatchAuthEvent(type, detail = null) {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(type, { detail }));
    }
  } catch (_) {}
}


export function clearLoopAuthState(options = {}) {
  const keepPendingRegistration = options.keepPendingRegistration === true;

  // Also clear the native restore marker. Otherwise iOS can keep the
  // last native session alive and App.js may rehydrate it on visibility/focus.
  setNativeSessionRestoreAllowed(false);

  const removedKeys = clearAuthScopedStorage({ keepPendingRegistration });
  emitStorageScopeChanged({ reason: "clearLoopAuthState", removedKeys, keepPendingRegistration });

  try { delete window.__MANUAL_AUTH__; } catch (_) {}
  try { window.__MANUAL_AUTH__ = null; } catch (_) {}

  const detail = { loggedIn: false, user: null, source: "clearLoopAuthState" };
  dispatchAuthEvent("manual-auth-changed", null);
  dispatchAuthEvent("manual-auth-cleared", detail);
  dispatchAuthEvent("loop-auth-cleared", detail);
  dispatchAuthEvent("loop-auth-session-changed", { previous: null, next: "guest", reason: "clearLoopAuthState" });
}

export function clearManualAuth(options = {}) {
  clearLoopAuthState(options);
}

export function persistManualAuth(obj) {
  setNativeSessionRestoreAllowed(true);
  try {
    localStorage.setItem("__MANUAL_AUTH__", JSON.stringify(obj));
  } catch (_) {}

  try {
    window.__MANUAL_AUTH__ = obj;
  } catch (_) {}

  const session = getEffectiveManualAuthSession();
  dispatchAuthEvent("manual-auth-changed", obj);
  dispatchAuthEvent("manual-auth-detected", obj);
  dispatchAuthEvent("loop-auth-login-success", session);
  dispatchAuthEvent("loop-auth-session-changed", { previous: "guest", next: session?.user?.uid ? `uid:${session.user.uid}` : "manual", session });
}

// Validity rules:
// - Requires idToken
// - Prefer JWT exp when available (including native iOS sessions)
// - Else, if obtainedAt/expiresIn exist, use them
// - If no timing info exists at all, assume valid as a last resort
export function isManualAuthLikelyValid(ma) {
  try {
    if (!ma || !ma.idToken) return false;

    // 1) Best source of truth: JWT exp
    const jwtExpiryMs = getJwtExpiryMs(ma.idToken);
    if (Number.isFinite(jwtExpiryMs)) {
      return Date.now() < (jwtExpiryMs - 60 * 1000); // 60s leeway
    }

    // 2) Fallback: obtainedAt + expiresIn
    if (ma.obtainedAt && ma.expiresIn) {
      const obtained = new Date(ma.obtainedAt).getTime();
      const expSec = parseInt(ma.expiresIn, 10);

      if (Number.isFinite(obtained) && Number.isFinite(expSec)) {
        const expiresAt = obtained + expSec * 1000;
        return Date.now() < (expiresAt - 60 * 1000);
      }
    }

    // 3) No timing data means we cannot trust the session on native iOS.
    // This prevents ghost sessions after logout/app restore.
    return false;
  } catch (_) {
    return false;
  }
}

export function getManualAuthStatus() {
  const ma = getManualAuth();
  if (!ma) return { exists: false, valid: false, reason: "missing" };

  const jwtExpiryMs = getJwtExpiryMs(ma.idToken);
  if (Number.isFinite(jwtExpiryMs)) {
    return {
      exists: true,
      valid: Date.now() < (jwtExpiryMs - 60 * 1000),
      reason: "jwt-exp",
      expiresAt: new Date(jwtExpiryMs).toISOString(),
    };
  }

  if (ma.obtainedAt && ma.expiresIn) {
    const obtained = new Date(ma.obtainedAt).getTime();
    const expSec = parseInt(ma.expiresIn, 10);
    if (Number.isFinite(obtained) && Number.isFinite(expSec)) {
      const expiresAt = obtained + expSec * 1000;
      return {
        exists: true,
        valid: Date.now() < (expiresAt - 60 * 1000),
        reason: "obtainedAt-expiresIn",
        expiresAt: new Date(expiresAt).toISOString(),
      };
    }
  }

  return {
    exists: true,
    valid: false,
    reason: "missing-expiry",
    expiresAt: null,
  };
}
